import { configureStore as configureStoreReduxToolkit } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { FLUSH, PAUSE, PERSIST, PURGE, persistReducer, persistStore, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { createStateSyncMiddleware, withReduxStateSync } from 'redux-state-sync';
import { authApi } from 'services/auth';
import { commandApi } from 'services/command';
import { hubsApi } from 'services/hubs';
import { missionV2Api } from 'services/missionV2';
import { missionV3Api } from 'services/missionV3';
import { reverseGeocodeApi } from 'services/reverseGeocode';
import { routesApi } from 'services/routes';
import { systemFleetsApi } from 'services/systemAdmin/fleets';
import { systemTeamsApi } from 'services/systemAdmin/teams';
import { userApi } from 'services/user';
import { vehiclesApi } from 'services/vehicles';
import { vehiclesV2Api } from 'services/vehiclesV2';

import { systemApi } from '../../services/system';
import { rootReducer } from '../reducers';
import { listenerMiddleware } from './listenerMiddleware';

const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['app', 'user', 'config', 'mrm', 'userSettings'],
    blacklist: [
        'hubs',
        'reverseGeocode',
        'roadRestrictions',
        'vehicles',
        'command',
        'app.pages',
        'app.isCameraStreamMaximized',
        'app.selectedCamera',
    ],
};

const reducer = persistReducer(persistConfig, withReduxStateSync(rootReducer));

export const configureStore = (preloadedState?: any) =>
    configureStoreReduxToolkit({
        preloadedState,
        reducer,
        // FIXME
        // @ts-expect-error
        middleware: getDefaultMiddleware =>
            getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                },
            }).concat(
                hubsApi.middleware,
                vehiclesApi.middleware,
                vehiclesV2Api.middleware,
                userApi.middleware,
                systemTeamsApi.middleware,
                systemFleetsApi.middleware,
                routesApi.middleware,
                missionV2Api.middleware,
                missionV3Api.middleware,
                commandApi.middleware,
                reverseGeocodeApi.middleware,
                listenerMiddleware.middleware,
                systemApi.middleware,
                authApi.middleware,
                createStateSyncMiddleware({
                    whitelist: [
                        'app/setSelectedVehicle',
                        'app/clearSelectedVIN',
                        'app/clearSelectedVehicle',
                        'app/setIsVehicleDetailsOpen',
                        'mrm/setIsMRMConfirmationDialogOpen',
                        'mrm/setIsReleaseConfirmationDialogOpen',
                        'mrm/setIsErrorDialogOpen',
                        'mrm/setIsSendingCommand',
                        'mrm/setIsAwaitingAnswer',
                        'assignMission/setConfirmationDialogOpen',
                        'assignMission/setIsErrorDialogOpen',
                        'cancelMission/setConfirmationDialogOpen',
                        'cancelMission/setIsAwaitingResponse',
                        'cancelMission/setErrorDialogOpen',
                        'resetMission/setConfirmationDialogOpen',
                        'resetMission/setIsAwaitingResponse',
                        'resetMission/setErrorDialogOpen',
                        'archiveMission/setConfirmationDialogOpen',
                        'archiveMission/setIsAwaitingResponse',
                        'archiveMission/setErrorDialogOpen',
                        'userSettings/setMeasurementSystem',
                    ],
                })
            ),
    });

export const store = configureStore();
export const persistor = persistStore(store);

/* Important: when changing or deleting existing state attributes, increment
  this number to force a full purge of any persisted state on app load.
   */
const STORE_VERSION = 16;

export const purgeIfVersionMismatch = () => {
    const purgeAndReload = () => {
        persistor.purge();
        window.location.reload();
    };
    const version = Number(localStorage.getItem('STORE_VERSION'));
    if (!version) {
        localStorage.setItem('STORE_VERSION', STORE_VERSION.toString());
        purgeAndReload();
        return;
    }

    if (version < STORE_VERSION) {
        localStorage.setItem('STORE_VERSION', STORE_VERSION.toString());
        console.info('[CC] Newer Store version detected. Purging persisted state.');
        purgeAndReload();
    }
};

// Infer the `RootState` and `RootDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type RootDispatch = typeof store.dispatch;

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);
